import { useEffect, useState } from 'react'
import { Input } from '@chakra-ui/react'
import {
  DialogRoot,
  DialogContent,
  DialogBody,
  DialogBackdrop,
  DialogHeader,
} from '@/components/ui/dialog'
import dayjs, { Dayjs } from 'dayjs'
import isBetween from 'dayjs/plugin/isBetween'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import TimeSelector from '@/_components/Time/TimeSelector'
import { IWorkHour } from '@/_types'

dayjs.extend(customParseFormat)
dayjs.extend(isSameOrBefore)
dayjs.extend(isBetween)

interface ITimePickerProps {
  max?: string | null // e.g. "18:00"
  min?: string | null // e.g. "09:00"
  value: Dayjs | null
  onChange: (time: Dayjs | null) => void
  disabled: boolean
  interval?: number
  isTodaySelected: boolean
  placeholder?: string
  open?: boolean
  onClose?: () => void
  onOpenFn?: () => void
  fromTime?: Dayjs | null
}

export default function CustomTimePicker({
  max = '23:59',
  min = '00:00',
  value,
  onChange,
  disabled,
  interval = 0,
  isTodaySelected,
  placeholder = '',
  open = false,
  onClose = () => { },
  onOpenFn = () => { },
  fromTime = null,
}: ITimePickerProps) {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedHour, setSelectedHour] = useState<number | null>(null)
  const [selectedMinute, setSelectedMinute] = useState<number | null>(null)

  const format = 'HH:mm'
  const workHourStart = dayjs(min, format, true)
  const workHourEnd = dayjs(max, format, true)
  const currentHour = dayjs().hour()

  useEffect(() => {
    if (value) {
      setSelectedHour(value.hour())
      setSelectedMinute(value.minute())
    }
  }, [value])

  useEffect(() => {
    if (open) {
      setIsModalOpen(true)
      onOpenFn()
    }
  }, [open])

  const isTimeDisabled = (selectedTime: Dayjs) => {

    // Check if time is within working hours
    const isWithinWorkingHours = selectedTime.isBetween(
      workHourStart,
      workHourEnd,
      'minute',
      '[]'
    )

    // Check if time is after fromTime if it exists
    const isAfterFromTime = fromTime
      ? selectedTime.isAfter(fromTime, 'minute')
      : true

    // Check if time is after current time if today is selected
    const isAfterCurrentTime = isTodaySelected
      ? selectedTime.isAfter(dayjs(), 'minute') ||
      selectedTime.isSame(dayjs(), 'minute')
      : true

    return !isWithinWorkingHours || !isAfterFromTime || !isAfterCurrentTime
  }

  const handleTimeSelect = (time: string) => {
    const selectedTime = dayjs(time)
    if (!isTimeDisabled(selectedTime)) {
      onChange(selectedTime)
      setIsModalOpen(false)
      onClose()
    }
  }

  const handleClose = () => {
    setIsModalOpen(false)
    onClose()
  }

  const displayValue = value ? value.format('HH:mm') : ''

  // Derive selected day from value (reservation form uses reserve_date + time; value carries the date)
  const selectedDay = value ? value.startOf('day') : dayjs()
  const workHours =
    min && max
      ? ({ from_time: min, to_time: max } as IWorkHour)
      : null

  // TimeSelector expects value as "YYYY-MM-DD HH:mm:00" for selection highlight
  const timeSelectorValue = value
    ? selectedDay.format('YYYY-MM-DD') + ' ' + value.format('HH:mm') + ':00'
    : ''

  return (
    <>
      <Input
        value={displayValue}
        onClick={() => !disabled && setIsModalOpen(true)}
        readOnly
        disabled={disabled}
      />

      {/* <Time open={isModalOpen} onOpen={() => setIsModalOpen(true)}
        onClose={() => setIsModalOpen(false)} onConfirm={() => { }}
        interval={interval} showTrigger={false}
        formattedTime={displayValue}
      /> */}

      <DialogRoot open={isModalOpen} onOpenChange={handleClose} size='xs' scrollBehavior='inside'>
        <DialogBackdrop />
        <DialogContent color={'text'}>
          <DialogHeader>{placeholder}</DialogHeader>
          <DialogBody p={4}>
            <TimeSelector
              interval={interval}
              value={timeSelectorValue}
              isToday={isTodaySelected}
              selectedDay={selectedDay}
              workHours={workHours}
              fromTime={fromTime}
              onChange={handleTimeSelect}
            />
          </DialogBody>
        </DialogContent>
      </DialogRoot>
    </>
  )
}
